import { supabase } from '../../../lib/supabase';
import type { Medicine, MedicineFormData } from '../types';

export const medicineService = {
  async getAll(): Promise<Medicine[]> {
    const { data, error } = await supabase
      .from('products')
      .select('*')
      .order('name')
      .limit(25000); // Ensure we load all medicines (current count: ~20k)
    
    if (error) throw error;
    
    return (data || []).map(product => ({
      id: product.id,
      name: product.name,
      genericName: product.generic_name,
      brandName: product.brand_name,
      strength: product.strength,
      form: product.form,
      route: product.route,
      manufacturer: product.manufacturer,
      reorderLevel: product.reorder_level || 0,
      status: product.status,
      createdAt: product.created_at,
      updatedAt: product.updated_at,
    }));
  },

  async getById(id: string): Promise<Medicine | null> {
    const { data, error } = await supabase
      .from('products')
      .select('*')
      .eq('id', id)
      .single();
    
    if (error) {
      if (error.code === 'PGRST116') return null;
      throw error;
    }
    
    return {
      id: data.id,
      name: data.name,
      genericName: data.generic_name,
      brandName: data.brand_name,
      strength: data.strength,
      form: data.form,
      route: data.route,
      manufacturer: data.manufacturer,
      reorderLevel: data.reorder_level || 0,
      status: data.status,
      createdAt: data.created_at,
      updatedAt: data.updated_at,
    };
  },

  async create(data: MedicineFormData): Promise<Medicine> {
    const { data: product, error } = await supabase
      .from('products')
      .insert([{
        name: data.name,
        generic_name: data.genericName,
        brand_name: data.brandName,
        strength: data.strength,
        form: data.form,
        route: data.route,
        manufacturer: data.manufacturer,
        reorder_level: data.reorderLevel,
        status: data.status,
      }])
      .select()
      .single();
    
    if (error) throw error;
    
    return {
      id: product.id,
      name: product.name,
      genericName: product.generic_name,
      brandName: product.brand_name,
      strength: product.strength,
      form: product.form,
      route: product.route,
      manufacturer: product.manufacturer,
      reorderLevel: product.reorder_level || 0,
      status: product.status,
      createdAt: product.created_at,
      updatedAt: product.updated_at,
    };
  },

  async update(id: string, data: Partial<MedicineFormData>): Promise<Medicine | null> {
    const updateData: any = {};
    if (data.name !== undefined) updateData.name = data.name;
    if (data.genericName !== undefined) updateData.generic_name = data.genericName;
    if (data.brandName !== undefined) updateData.brand_name = data.brandName;
    if (data.strength !== undefined) updateData.strength = data.strength;
    if (data.form !== undefined) updateData.form = data.form;
    if (data.route !== undefined) updateData.route = data.route;
    if (data.manufacturer !== undefined) updateData.manufacturer = data.manufacturer;
    if (data.reorderLevel !== undefined) updateData.reorder_level = data.reorderLevel;
    if (data.status !== undefined) updateData.status = data.status;
    
    const { data: product, error } = await supabase
      .from('products')
      .update(updateData)
      .eq('id', id)
      .select()
      .single();
    
    if (error) {
      if (error.code === 'PGRST116') return null;
      throw error;
    }
    
    return {
      id: product.id,
      name: product.name,
      genericName: product.generic_name,
      brandName: product.brand_name,
      strength: product.strength,
      form: product.form,
      route: product.route,
      manufacturer: product.manufacturer,
      reorderLevel: product.reorder_level || 0,
      status: product.status,
      createdAt: product.created_at,
      updatedAt: product.updated_at,
    };
  },

  async search(query: string, status?: 'all' | 'active' | 'inactive'): Promise<Medicine[]> {
    let queryBuilder = supabase
      .from('products')
      .select('*')
      .or(`name.ilike.%${query}%,generic_name.ilike.%${query}%,brand_name.ilike.%${query}%,manufacturer.ilike.%${query}%`)
      .order('name');
    
    if (status && status !== 'all') {
      queryBuilder = queryBuilder.eq('status', status);
    }
    
    const { data, error } = await queryBuilder;
    
    if (error) throw error;
    
    return (data || []).map(product => ({
      id: product.id,
      name: product.name,
      genericName: product.generic_name,
      brandName: product.brand_name,
      strength: product.strength,
      form: product.form,
      route: product.route,
      manufacturer: product.manufacturer,
      reorderLevel: product.reorder_level || 0,
      status: product.status,
      createdAt: product.created_at,
      updatedAt: product.updated_at,
    }));
  },

  async getActiveOnly(): Promise<Medicine[]> {
    const { data, error } = await supabase
      .from('products')
      .select('*')
      .eq('status', 'active')
      .order('name')
      .limit(25000); // Ensure we load all active medicines
    
    if (error) throw error;
    
    return (data || []).map(product => ({
      id: product.id,
      name: product.name,
      genericName: product.generic_name,
      brandName: product.brand_name,
      strength: product.strength,
      form: product.form,
      route: product.route,
      manufacturer: product.manufacturer,
      reorderLevel: product.reorder_level || 0,
      status: product.status,
      createdAt: product.created_at,
      updatedAt: product.updated_at,
    }));
  },

  async checkDuplicate(name: string): Promise<boolean> {
    const { data, error } = await supabase
      .from('products')
      .select('id')
      .eq('name', name)
      .limit(1);
    
    if (error) throw error;
    return (data || []).length > 0;
  }
};
